import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { StyledModalFooter } from 'LuxKit/Modal/ModalFooter'
import Group from 'components/utils/Group'
import FormatCurrency from 'components/Common/FormatCurrency'
import TextInput from 'components/Common/Form/Input/TextInput'
import TextArea from 'components/Common/Form/Input/TextArea'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useRebrandLightSurfaceButtonVariant from 'hooks/useRebrandLightSurfaceButtonVariant'
import { rem } from 'polished'
import styled from 'styled-components'
import { WeddingGiftType, type Wedding, type WeddingGift } from 'weddings/api/weddings'
import WeddingGiftContributionProgress from './WeddingGiftContributionProgress'
import Clickable from 'components/Common/Clickable'
import clsx from 'clsx'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import Image from 'components/Common/Image'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getWeddingGiftCategoryLabel } from 'weddings/utils/weddingGiftLabels'

const MIN_AMOUNT = 50
const MESSAGE_MAX_LENGTH = 200

const AmountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const AmountOption = styled(Clickable)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(2)};
  padding: ${rem(8)} ${rem(20)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  cursor: pointer;
  text-align: center;
  transition: border-color 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};

  &:hover {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &.selected {
    border: 2px solid ${props => props.theme.palette.brand.primary.normal};
  }
`

const FooterLeft = styled(Group)`
  flex: 1;
`

const Content = styled(Group)`
  .flex-1 {
    flex: 1;
  }
`

const GiftContainer = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  column-gap: ${rem(8)};
  display: grid;
  flex: 1;
  grid-template-columns: ${rem(102)} minmax(0, 1fr);
`

const GiftContentWrapper = styled(Group)`
  min-width: 0;
  padding: ${rem(12)} 0;
`

const GiftImage = styled(Image)`
  align-self: stretch;
  display: block;
  height: 100%;
  min-height: ${rem(96)};
  width: 100%;
`

interface Props {
  gift?: WeddingGift
  wedding: Wedding
  amount?: number
  currency: string
  isFlexibleAmount?: boolean
}

interface AmountPreset {
  value: number
  label: string
}

interface WeddingContributeGift extends Omit<WeddingGift, 'id'> {
  id?: string
}

export interface ContributeGiftModalResult {
  amount: number
  message: string
  firstName: string
  lastName: string
  gift: WeddingContributeGift
}

function buildPresets(giftAmount: number, hasContributions: boolean, intl: ReturnType<typeof useIntl>): Array<AmountPreset> {
  const presets = [
    { value: MIN_AMOUNT, label: intl.formatMessage({ defaultMessage: 'A round of drinks', id: 'WOEIW30ij6HyuolZwUi8', description: 'Preset contribution amount label in the wedding gift contribution modal.' }) },
    {
      value: Math.round(giftAmount / 4),
      label: hasContributions ?
          intl.formatMessage({ defaultMessage: 'A quarter of the remaining', id: 'ANHII4fkbz1_CcPOIdWn', description: 'Preset contribution label for one quarter of a partially funded wedding gift.' }) :
          intl.formatMessage({ defaultMessage: 'A quarter of it', id: 'vKST3v4nnpjnt6mj80VZ', description: 'Preset contribution label for one quarter of an unfunded wedding gift.' }),
    },
    {
      value: Math.round(giftAmount / 2),
      label: hasContributions ?
          intl.formatMessage({ defaultMessage: 'Half of the remaining', id: '5tbWt_tgh6CVc-nr5NUt', description: 'Preset contribution label for half of a partially funded wedding gift.' }) :
          intl.formatMessage({ defaultMessage: 'Half of it', id: 'LRbfr7sRNSKx87nDbzyf', description: 'Preset contribution label for half of an unfunded wedding gift.' }),
    },
    {
      value: giftAmount,
      label: hasContributions ?
          intl.formatMessage({ defaultMessage: 'Remaining amount', id: 'HIXBgYarHnKSlpN1t9zV', description: 'Preset contribution label for the remaining amount of a partially funded gift.' }) :
          intl.formatMessage({ defaultMessage: 'The whole thing', id: 'y7QO1ws_IkRtR6j_hR9W', description: 'Preset contribution label for funding an entire wedding gift.' }),
    },
  ]
  const filteredPresets = presets.filter(preset => preset.value >= MIN_AMOUNT)
  const uniquePresets = [...new Map(filteredPresets.map(preset => [preset.value, preset])).values()]
  if (!uniquePresets.length) return [presets[presets.length - 1]]
  return uniquePresets
}

function WeddingContributeGiftModal(props: Props) {
  const { gift, wedding, amount, currency, isFlexibleAmount } = props
  const intl = useIntl()
  const { resolve } = useModalElementContext<ContributeGiftModalResult | undefined>()
  const lightSurfaceButtonVariant = useRebrandLightSurfaceButtonVariant()

  const formRef = useRef<HTMLFormElement>(null)

  const givenName = useAppSelector(state => state.auth.account.givenName) ?? ''
  const surname = useAppSelector(state => state.auth.account.surname) ?? ''

  const currencyFormatter = useCurrencyFormatter({ currency })
  const formattedMinAmount = currencyFormatter(MIN_AMOUNT)

  const progress = gift?.contributions?.progress ?? 0
  const contributorsCount = gift?.contributions?.contributorsCount ?? 0
  const giftAmount = gift?.amount ?? 0
  const remainingAmount = isFlexibleAmount && progress > 0 ? Math.round(giftAmount * (1 - progress / 100)) : giftAmount

  const presets = isFlexibleAmount ? buildPresets(remainingAmount, progress > 0, intl) : []
  const [selectedPreset, setSelectedPreset] = useState<number>(remainingAmount)
  const [customAmount, setCustomAmount] = useState(amount?.toString() ?? '')

  const selectedAmount = customAmount ? Number(customAmount) : selectedPreset

  const handlePresetClick = useCallback((value: number) => {
    setSelectedPreset(value)
    setCustomAmount('')
  }, [])

  const handleCustomAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomAmount(e.target.value)
    setSelectedPreset(0)
  }, [])

  const handleClose = useCallback(() => {
    resolve(undefined)
  }, [resolve])

  const handleConfirm = useCallback(() => {
    formRef.current?.requestSubmit()
  }, [])

  const giftData = useMemo(() => {
    if (!gift) {
      return {
        imageId: 'gift-card_zk7nap',
        title: intl.formatMessage({ defaultMessage: 'Luxury Escapes gift card', id: 'nNyLl2wrWUME7g0dWiZg', description: 'Fallback title shown for a wedding gift card contribution.' }),
        category: 'ANY',
        type: WeddingGiftType.GIFT_CARD,
        amount: amount ?? 0,
        currency,
      } satisfies WeddingContributeGift
    }

    return gift
  }, [gift, amount, currency, intl])

  const handleSubmit = useCallback(async(e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const form = formRef.current
    if (!form) return
    const data = new FormData(form)
    const message = (data.get('message') as string).trim()
    const firstName = (data.get('firstName') as string).trim()
    const lastName = (data.get('lastName') as string).trim()

    if (!selectedAmount) return

    resolve({
      amount: selectedAmount,
      message: message,
      firstName: firstName,
      lastName: lastName,
      gift: giftData,
    })
  }, [resolve, selectedAmount, giftData])

  return (
    <ModalBase onClose={handleClose}>
      <ModalHeader
        title={intl.formatMessage(
          { defaultMessage: 'Gift this to {partnerOneFirstName} & {partnerTwoFirstName}', id: 'cKf29TSKY4-4bgEJ6YwQ', description: 'Title for the modal where a guest contributes to a wedding gift.' },
          { partnerOneFirstName: wedding.partnerOneFirstName, partnerTwoFirstName: wedding.partnerTwoFirstName },
        )}
        onCloseButtonClick={handleClose}
      />
      <ModalBody>
        <ModalContent>
          <form ref={formRef} onSubmit={handleSubmit}>
            <Content direction="vertical" gap={20}>
              <GiftContainer>
                <GiftImage
                  id={giftData.imageId}
                  alt={giftData.title}
                  width={102}
                  fit="center"
                  objectFit="cover"
                />
                <GiftContentWrapper direction="vertical" gap={12} verticalAlign="center" className="flex-1">
                  <Group direction="vertical" gap={0}>
                    <BodyText variant="small">{getWeddingGiftCategoryLabel(intl, giftData.category)}</BodyText>
                    <BodyText variant="medium" weight="bold" lineClamp={3}>{giftData.title}</BodyText>
                  </Group>
                  <Heading variant="heading4"><FormatCurrency value={giftData.amount} currency={currency} /></Heading>
                </GiftContentWrapper>
              </GiftContainer>

              {isFlexibleAmount && <WeddingGiftContributionProgress progress={progress} contributorsCount={contributorsCount} />}

              {presets.length > 0 && <Group direction="vertical" gap={8}>
                <BodyText variant="medium" weight="bold">
                  {intl.formatMessage({ defaultMessage: 'Select an amount', id: 'SmwgLrNdt5thKhU1Cl97', description: 'Heading for preset contribution amounts in the wedding gift modal.' })}
                </BodyText>
                <AmountGrid>
                  {presets.map(preset => (
                    <AmountOption
                      key={preset.value}
                      className={clsx({ selected: selectedPreset === preset.value && !customAmount })}
                      onClick={() => handlePresetClick(preset.value)}
                    >
                      <BodyText variant="medium" weight="bold">
                        <FormatCurrency value={preset.value} currency={currency} />
                      </BodyText>
                      <Caption variant="medium" colour="light-foreground-alt">{preset.label}</Caption>
                    </AmountOption>
                  ))}
                </AmountGrid>
              </Group>}

              <TextInput
                label={intl.formatMessage({ defaultMessage: 'Or enter your amount', id: '8nZDZ0wxLr8DXJvRHjWZ', description: 'Label for custom amount input in the wedding gift contribution modal.' })}
                placeholder={intl.formatMessage(
                  { defaultMessage: 'Min. {formattedMinAmount}', id: 'Abn9LXdE9hRxlU7VMxsU', description: 'Placeholder showing minimum custom gift contribution amount.' },
                  { formattedMinAmount },
                )}
                type="number"
                min={MIN_AMOUNT}
                value={customAmount}
                onChange={handleCustomAmountChange}
              />

              <Group direction="horizontal" gap={16}>
                <TextInput
                  name="firstName"
                  label={intl.formatMessage({ defaultMessage: 'First name', id: 'OAaBwC_lc59LdCb5QHrX', description: 'Label for contributor first name input in the wedding gift modal.' })}
                  placeholder={intl.formatMessage({ defaultMessage: 'First name', id: 'ZgWqEMYBalZarbn-xTQb', description: 'Placeholder for contributor first name input in the wedding gift modal.' })}
                  defaultValue={givenName}
                  required
                  requiredErrorMessage={intl.formatMessage({ defaultMessage: 'First name is required', id: 'KdJMrYnej46ok8YAhFck', description: 'Validation message when contributor first name is missing.' })}
                  className="flex-1"
                />
                <TextInput
                  name="lastName"
                  label={intl.formatMessage({ defaultMessage: 'Last name', id: 'uiUT82Q5FCn6tg9TfTF7', description: 'Label for contributor last name input in the wedding gift modal.' })}
                  placeholder={intl.formatMessage({ defaultMessage: 'Last name', id: 'Nls8ROnrDYXw3EUPnykm', description: 'Placeholder for contributor last name input in the wedding gift modal.' })}
                  defaultValue={surname}
                  required
                  requiredErrorMessage={intl.formatMessage({ defaultMessage: 'Last name is required', id: 'LSd7d_ZtLhXIU8Oy_18T', description: 'Validation message when contributor last name is missing.' })}
                  className="flex-1"
                />
              </Group>

              <TextArea
                name="message"
                label={intl.formatMessage(
                  { defaultMessage: 'A private message for {partnerOneFirstName} & {partnerTwoFirstName}', id: '1qX0H_n2QSELvT6HxASi', description: 'Label for private message input in the wedding gift contribution modal.' },
                  { partnerOneFirstName: wedding.partnerOneFirstName, partnerTwoFirstName: wedding.partnerTwoFirstName },
                )}
                placeholder={intl.formatMessage({ defaultMessage: 'The couple will receive an email with the gift voucher.', id: 'cc6PxazBsZMpS1CKedba', description: 'Placeholder explaining where a wedding gift contribution message appears.' })}
                maxLength={MESSAGE_MAX_LENGTH}
                rows={4}
                helpText={intl.formatMessage(
                  { defaultMessage: 'Max. {maxLength} characters', id: 'M6XP7Y3U8UycCB5cuJ26', description: 'Help text showing maximum character count for wedding gift message.' },
                  { maxLength: MESSAGE_MAX_LENGTH },
                )}
              />
            </Content>
          </form>
        </ModalContent>
      </ModalBody>

      <StyledModalFooter>
        <Group direction="horizontal" gap={16} verticalAlign="center" horizontalAlign="space-between">
          <FooterLeft direction="vertical" gap={2}>
            {selectedAmount > 0 && (
              <Heading variant="heading5">
                <FormatCurrency value={selectedAmount} currency={currency} />
              </Heading>
            )}
          </FooterLeft>
          <TextButton
            kind="primary"
            variant={lightSurfaceButtonVariant}
            size="large"
            disabled={!selectedAmount || selectedAmount < MIN_AMOUNT}
            onClick={handleConfirm}
          >
            {intl.formatMessage({ defaultMessage: 'Confirm', id: '3cISnULEzEJJumIMXoJm', description: 'Primary action button for confirming a wedding gift contribution.' })}
          </TextButton>
        </Group>
      </StyledModalFooter>
    </ModalBase>
  )
}

export default WeddingContributeGiftModal
